import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  Form,
  Input,
  message,
  Card,
  Typography,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import { COLOR_PRIMARY } from '../../utils/colors';

const { Title, Text } = Typography;

const OpcClients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editingKey, setEditingKey] = useState(null);
  const [editForm] = Form.useForm();
  const [addForm] = Form.useForm();
  const firstFieldRef = useRef(null);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.opcClients.getAll();
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch {
      message.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const isEditing = (record) => record._id === editingKey;

  const startEdit = (record) => {
    editForm.setFieldsValue({ name: record.name });
    setEditingKey(record._id);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    editForm.resetFields();
  };

  const saveEdit = async (record) => {
    try {
      const values = await editForm.validateFields();
      await apiService.opcClients.update(record._id, values);
      setEditingKey(null);
      message.success('Saved');
      fetchClients();
    } catch (e) {
      if (e?.response?.data?.message) message.error(e.response.data.message);
    }
  };

  const handleAdd = async () => {
    try {
      const values = await addForm.validateFields();
      await apiService.opcClients.create(values);
      addForm.resetFields();
      message.success('Client added');
      fetchClients();
      setTimeout(() => firstFieldRef.current?.focus(), 100);
    } catch (e) {
      if (e?.response?.data?.message) message.error(e.response.data.message);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (v, record) =>
        isEditing(record) ? (
          <Form.Item name="name" noStyle rules={[{ required: true, message: ' ' }]}>
            <Input size="small" style={{ width: 240 }} placeholder="Client name" />
          </Form.Item>
        ) : (
          <Text strong>{v}</Text>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 72,
      align: 'right',
      render: (_, record) =>
        isEditing(record) ? (
          <Space size={2}>
            <Button type="text" size="small" icon={<CheckOutlined style={{ color: '#3f8600' }} />} onClick={() => saveEdit(record)} />
            <Button type="text" size="small" icon={<CloseOutlined />} onClick={cancelEdit} />
          </Space>
        ) : (
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEdit(record)} />
        ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Clients</Title>
      </div>

      {/* Add form */}
      <Card
        size="small"
        style={{ marginBottom: 12, borderTop: `2px solid ${COLOR_PRIMARY}`, background: 'rgba(22,119,255,0.06)' }}
        bodyStyle={{ padding: '10px 12px' }}
      >
        <Form form={addForm} onFinish={handleAdd}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={labelStyle}>Name *</div>
              <Form.Item name="name" noStyle rules={[{ required: true, message: ' ' }]}>
                <Input
                  ref={firstFieldRef}
                  size="small"
                  style={{ width: 240 }}
                  placeholder="Client name"
                  onPressEnter={handleAdd}
                />
              </Form.Item>
            </div>
            <div style={{ alignSelf: 'flex-end' }}>
              <Button type="primary" size="small" htmlType="submit" icon={<PlusOutlined />}>Add</Button>
            </div>
          </div>
        </Form>
      </Card>

      {/* Clients table */}
      <Form form={editForm} component={false}>
        <Table
          dataSource={clients}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={false}
          size="small"
          style={{ borderRadius: 8, overflow: 'hidden' }}
          locale={{ emptyText: 'No clients yet — add one above' }}
        />
      </Form>
    </div>
  );
};

const labelStyle = { fontSize: 11, color: '#888', marginBottom: 2 };

export default OpcClients;
